// /managers/ConvoManager/UniversalResponseSchema.js
/**
 * @file UniversalResponseSchema.js
 * 
 * Defines the standardized "Universal Response Schema" for all server-to-client
 * communications, particularly for streaming chat responses. This schema serves as
 * the single source of truth for the structure of data chunks sent from the server.
 * 
 * All client-side parsing and state management should be built to conform to
 * the event types and data structures defined herein.
 */

/**
 * An enumeration of all possible stream event types, aligned with the server's output.
 * Using this enum ensures consistency across the application.
 * @enum {string}
 */
export const StreamEventTypes = {
  // Core Content Events
  STREAM_START: 'STREAM_START',
  STREAM_END: 'STREAM_END',
  
  // Structured Block Events from Server-Side Parser (Primary content method)
  BLOCK_CREATE: 'BLOCK_CREATE',
  BLOCK_DELTA: 'BLOCK_DELTA',
  BLOCK_END: 'BLOCK_END',

  // Tool/Function Call Lifecycle Events (from LLM)
  TOOL_CALL_START: 'TOOL_CALL_START',
  TOOL_CALL_DELTA: 'TOOL_CALL_DELTA', // Note: Not all platforms support this
  TOOL_CALL_END: 'TOOL_CALL_END',

  // Internal Processing Events (Generated by our backend, not the LLM directly)
  TOOL_PROCESSING_START: 'TOOL_PROCESSING_START',
  TOOL_PROCESSING_RESULT: 'TOOL_PROCESSING_RESULT',
  
  // Memory Context Events
  MEMORY_CONTEXT_START: 'MEMORY_CONTEXT_START',
  MEMORY_CONTEXT_DELTA: 'MEMORY_CONTEXT_DELTA',
  MEMORY_CONTEXT_END: 'MEMORY_CONTEXT_END',

  // Metadata and Status Events
  IDS: 'IDS',
  RESPONSE_METADATA: 'RESPONSE_METADATA',
  USAGE_UPDATE: 'USAGE_UPDATE',
  TRANSCRIPTION_UPDATE: 'TRANSCRIPTION_UPDATE',
  STREAM_ERROR: 'STREAM_ERROR',
};

/**
 * A type definition for a single stream event object.
 * @typedef {object} StreamEvent
 * @property {StreamEventTypes} type - The type of the event.
 * @property {*} payload - The payload of the event.
 */

/*
  EVENT PAYLOAD STRUCTURES:
  
  Core Content:
  - STREAM_START: { payload: { platform: string, model: string } }
  - STREAM_END: { payload: { reason: string } }
  
  Structured Blocks (from server-side parser):
  - BLOCK_CREATE: { payload: { id: string, type: 'text'|'code'|'document'|'thinking'|'image', ...otherProps } }
  - BLOCK_DELTA: { payload: { id: string, contentChunk: string } }
  - BLOCK_END: { payload: { id: string } }
  
  Tool Calls:
  - TOOL_CALL_START: { payload: { toolCallId: string, toolName: string } }
  - TOOL_CALL_DELTA: { payload: { toolCallId: string, argumentChunk: string } }
  - TOOL_CALL_END: { payload: { toolCallId: string, input: string } }
  
  Tool Processing:
  - TOOL_PROCESSING_START: { payload: { toolCallId: string, toolName: string } }
  - TOOL_PROCESSING_RESULT: { payload: { toolCallId: string, toolName: string, isError: boolean, result?: any } }

  Memory Context:
  - MEMORY_CONTEXT_START: { payload: { header: string } }
  - MEMORY_CONTEXT_DELTA: { payload: { contentChunk: string, memoryId?: string } }
  - MEMORY_CONTEXT_END: { payload: { footer: string } }

  Metadata:
  - IDS: { payload: { userMessageId: string, assistantMessageId: string, requestId: string, platformHandler: string } }
  - RESPONSE_METADATA: { payload: { metadata: object } }
  - USAGE_UPDATE: { payload: { promptTokens: number, responseTokens: number, totalTokens: number } }
  
  Errors:
  - STREAM_ERROR: { payload: { message: string, code: string, status?: number } }
*/

export default StreamEventTypes;